/**
 * Rozhodnutí, jestli chybová odpověď znamená **neplatný token**, a tedy důvod uživatele odhlásit.
 *
 * Bylo to napsané na třech místech zvlášť, pokaždé o kousek jinak, a jedna z těch variant
 * odhlašovala i na `status: 0` (DEV-2133). Proto je to teď jedno místo — když se bude
 * rozhodovat znovu, ať se rozhoduje jednou.
 *
 * ## Proč `status: 0` není důvod k odhlášení
 *
 * `status: 0` neznamená, že token neplatí. Je to **přerušený nebo nedoručený request** —
 * vypadlá wifi, uspaný notebook, request zablokovaný rozšířením prohlížeče, CORS. Token je
 * v tu chvíli pořád platný, jen se odpověď nevrátila. Refresh endpoint navíc bere refresh
 * token z cookie, ne access token, takže projde i po expiraci — další tick to dotáhne.
 *
 * Odhlásit uživatele na vypadlé wifi je horší chyba než ta, kterou to mělo řešit: kdo zrovna
 * nic needitoval, přijde o rozpracovaný formulář. Aktivnímu uživateli se stejně nic nezmění —
 * první další API call vrátí 401 a odhlásí ho `ErrorHandlingInterceptorService`.
 *
 * Ve stejném duchu `status: 0` zahazujeme i v Sentry filtrech (DEV-733).
 *
 * ## Proč 400 s chybějící cookie taky ne (DEV-2270)
 *
 * `onStartup()` se ptá „jsem přihlášený?" tak, že prostě zkusí refresh a selhání si přeloží
 * jako „ne". Když cookie s refresh tokenem není, request dojde prázdný. Frontend to nemá jak
 * předem poznat — refresh token je httpOnly a NgRx stav je po reloadu prázdný, takže ta sonda
 * je jediný mechanismus, který aplikace má.
 *
 * `ca-auth` ale ty dva případy rozlišuje, a každý vzniká na jiné vrstvě (naměřeno 2. 9. 2026
 * proti `dev.aws.cleverassets.cz`):
 *
 * | co odejde | status | tělo |
 * |---|---|---|
 * | bez `refreshToken` cookie | **400** | `Required cookie 'refreshToken' … is not present` |
 * | neplatná hodnota | **401** | `Invalid authorization token` |
 * | prázdná hodnota | **401** | `Invalid authorization token` |
 *
 * Ta 400 tedy pochází ze **Springové validace parametru**, ne z auth logiky — request je
 * odmítnutý dřív, než se aplikačního kódu dotkne. Server u něj vůbec nehledá, jestli nějaký
 * token existuje. Rušit kvůli tomu session je stejný omyl jako u `status: 0`.
 *
 * V nočním e2e běhu takových requestů odešlo 405, každý se vyhodnotil jako odhlášení.
 *
 * ## Proč se rozhoduje podle těla, a ne podle `status !== 400`
 *
 * Dva důvody, oba praktické:
 *
 * 1. **Ostatní 400 mají zůstat důvodem k odhlášení.** Tenhle predikát používá i
 *    `handleProjectLoadError`, kde 400 přijde z úplně jiného endpointu a znamená něco jiného.
 *    Podle statusu by se změnilo i to místo — a tam „neodhlásit" znamená aplikaci bez
 *    načteného projektu.
 * 2. **Fail-safe směrem k bezpečnosti.** V OAuth 2.0 (RFC 6749 §5.2) je 400 normální odpověď
 *    pro *mrtvý* refresh token (`invalid_grant`). Kdyby se `ca-auth` někdy k OAuthu přiblížil,
 *    neznámá 400 se pořád odhlásí a odvolaná session nezůstane žít. Kdyby Spring naopak
 *    přeformuloval svou hlášku, vrátíme se nejhůř k dnešnímu chování — tedy k nepříjemnosti,
 *    ne k děravé autorizaci.
 */

/** Hláška ze Springové validace chybějícího `@CookieValue`. Viz tabulka výše. */
const MISSING_REFRESH_COOKIE = /required cookie '?refreshtoken'?/i

/**
 * Tělo chybové odpovědi umí přijít v několika podobách — jako rozparsovaný objekt
 * v `error.error`, jako řetězec, nebo (u obalených chyb) rovnou na `error.message`.
 * DEV-1957 na téhle rozmanitosti už jednou zakopl, proto se čtou všechny tři.
 */
const errorMessageOf = (error): string => {
    const body = error?.error

    if (typeof body === 'string') {
        return body
    }

    return String(body?.message ?? error?.message ?? '')
}

const isMissingRefreshCookie = (error): boolean => MISSING_REFRESH_COOKIE.test(errorMessageOf(error))

export const isInvalidTokenError = (error: { status?: number }): boolean => {
    if (error?.status === 401) {
        return true
    }

    // Neznámá 400 odhlašuje jako dosud; výjimka je jen prázdný request bez refresh cookie.
    if (error?.status === 400) {
        return !isMissingRefreshCookie(error)
    }

    return false
}
